import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../../lib/api";
import { isSessionExpired } from "../../../lib/api";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { useEffectiveRole, canAtLeast } from "../../../lib/roles";
import {
  useImportRows,
  useImportDuplicates,
  useImportReadiness,
} from "../hooks/useImportRowsData";
import { RowsTable } from "../components/RowsTable";
import { DuplicateResolverDialog } from "../components/DuplicateResolverDialog";
import { CommitConfirmDialog } from "../components/CommitConfirmDialog";
import {
  NormalizedRow,
  RowStatus,
  ROW_STATUS_FILTERS,
  Resolution,
  offeredResolutionsFor,
} from "../lib/rowContract";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

type JobShape = {
  id: string;
  status: string;
  version: number;
  counts?: Record<string, number>;
};

const PAGE_SIZE = 50;

// Just the pre-commit statuses are user-facing filters during D3.
// Terminal statuses (CREATED/UPDATED/SKIPPED/FAILED) are shown post-commit
// (D4) but the endpoint accepts them today too.
const FILTER_TABS: Array<{ label: string; value: RowStatus | null }> = [
  { label: "All", value: null },
  { label: "Valid", value: "VALID" },
  { label: "Invalid", value: "INVALID" },
  { label: "Duplicate", value: "DUPLICATE" },
];

export function Step4Review({
  job,
  onJobUpdated,
}: {
  job: JobShape;
  onJobUpdated: () => void;
}) {
  const [filter, setFilter] = useState<RowStatus | null>(null);
  const [page, setPage] = useState(1);

  const rowsQ = useImportRows(job.id, { status: filter, page, limit: PAGE_SIZE });
  const dupsQ = useImportDuplicates(job.id);
  const readyQ = useImportReadiness(job.id);

  const [activeRow, setActiveRow] = useState<NormalizedRow | null>(null);
  const [activeSimple, setActiveSimple] = useState<NormalizedRow | null>(null);
  const [commitOpen, setCommitOpen] = useState(false);

  const refetchAll = async () => {
    // On any successful resolution we refetch server truth for job + rows
    // page + duplicates + readiness. We never mutate counts locally.
    onJobUpdated();
    await Promise.all([rowsQ.refetch(), dupsQ.refetch(), readyQ.refetch()]);
  };

  const onOpenRow = (row: NormalizedRow) => {
    if (!row.status) return; // unknown -> no action
    if (row.status === "DUPLICATE") setActiveRow(row);
    else if (row.status === "VALID" || row.status === "INVALID") setActiveSimple(row);
    // PENDING and terminal -> ignored
  };

  return (
    <div className="space-y-6" data-testid="step-4-review">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Review &amp; resolve</h2>
        <p className="text-sm text-slate-500 mt-1">
          Every row was dry-run validated on the server. Fix or annotate blockers below before continuing to commit.
        </p>
      </div>

      <SummaryCards
        counts={job.counts ?? {}}
        readiness={readyQ.readiness}
        loading={readyQ.loading}
      />

      <div className="flex items-center gap-2 flex-wrap" data-testid="rows-filters">
        {FILTER_TABS.map((t) => (
          <button
            key={t.label}
            onClick={() => {
              setFilter(t.value);
              setPage(1);
            }}
            aria-pressed={filter === t.value}
            className={`rounded-full border px-3 py-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 ${
              filter === t.value
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
            }`}
            data-testid={`rows-filter-${t.value ?? "all"}`}
          >
            {t.label}
          </button>
        ))}
        <div className="ml-auto text-xs text-slate-500" data-testid="rows-pagination-info">
          {rowsQ.data
            ? `${(rowsQ.data.page - 1) * rowsQ.data.limit + 1}–${Math.min(
                rowsQ.data.total,
                rowsQ.data.page * rowsQ.data.limit,
              )} of ${rowsQ.data.total}`
            : ""}
        </div>
      </div>

      {rowsQ.error && !isSessionExpired(rowsQ.error) && (
        <div
          role="alert"
          className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900"
          data-testid="rows-error"
        >
          Could not load rows. <button className="underline" onClick={() => rowsQ.refetch()}>Retry</button>
        </div>
      )}

      <RowsTable
        rows={rowsQ.data?.rows ?? []}
        loading={rowsQ.loading}
        onOpenRow={onOpenRow}
      />

      {rowsQ.data && rowsQ.data.total > PAGE_SIZE && (
        <div className="flex items-center gap-2 justify-end" data-testid="rows-pagination">
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="rows-prev"
          >
            Prev
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            disabled={page * PAGE_SIZE >= rowsQ.data.total}
            onClick={() => setPage((p) => p + 1)}
            data-testid="rows-next"
          >
            Next
          </Button>
        </div>
      )}

      {activeRow && (
        <DuplicateResolverDialog
          jobId={job.id}
          row={activeRow}
          duplicates={dupsQ.byRow[activeRow.id] ?? []}
          open
          onClose={() => setActiveRow(null)}
          onResolved={refetchAll}
        />
      )}
      {activeSimple && (
        <SimpleResolveDialog
          jobId={job.id}
          row={activeSimple}
          open
          onClose={() => setActiveSimple(null)}
          onResolved={refetchAll}
        />
      )}
      {commitOpen && (
        <CommitConfirmDialog
          jobId={job.id}
          job={job}
          open
          onClose={() => setCommitOpen(false)}
          onCommitted={onJobUpdated}
        />
      )}

      <div className="pt-2 border-t border-slate-100 flex items-center justify-end">
        <CommitStartCTA
          onOpen={() => setCommitOpen(true)}
          disabled={!readyQ.readiness.ready_for_commit}
          unresolvedTotal={readyQ.readiness.unresolved_total}
        />
      </div>
    </div>
  );
}

function CommitStartCTA({
  onOpen,
  disabled,
  unresolvedTotal,
}: {
  onOpen: () => void;
  disabled: boolean;
  unresolvedTotal: number;
}) {
  const role = useEffectiveRole();
  const canMutate = canAtLeast(role, "manager_trainer");
  if (!canMutate) {
    // Representative + unknown/null roles: read-only — the CTA is hidden.
    // Viewer never reaches this route (blocked by role/route guards).
    return (
      <span className="text-xs text-slate-500" data-testid="commit-cta-readonly">
        View only — a manager will start the import.
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={disabled}
      className="rounded-full bg-slate-900 text-white px-4 py-1.5 text-sm font-medium disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
      data-testid="open-commit-confirm-btn"
    >
      {!disabled
        ? "Start import"
        : `Resolve ${unresolvedTotal} row${unresolvedTotal === 1 ? "" : "s"} to commit`}
    </button>
  );
}

function SummaryCards({
  counts,
  readiness,
  loading,
}: {
  counts: Record<string, number>;
  readiness: {
    invalid: number;
    duplicates_unresolved: number;
    manual_review: number;
    unresolved_total: number;
    ready_for_commit: boolean;
  };
  loading: boolean;
}) {
  const valid = Number(counts.valid ?? 0);
  const invalid = Number(counts.invalid ?? readiness.invalid ?? 0);
  const duplicates = Number(counts.duplicates_detected ?? 0);
  const ready = readiness.ready_for_commit;

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="review-summary-cards">
      <Card testId="card-valid" label="Valid" value={valid} tone="ok" />
      <Card testId="card-invalid" label="Invalid" value={invalid} tone={invalid ? "bad" : "muted"} />
      <Card testId="card-duplicates" label="Duplicates" value={duplicates} tone={duplicates ? "warn" : "muted"} />
      <Card
        testId="card-manual-review"
        label="Manual review"
        value={readiness.manual_review}
        tone={readiness.manual_review ? "warn" : "muted"}
      />
      <Card
        testId="card-ready"
        label={ready ? "Ready for commit" : "Not ready"}
        value={loading ? "…" : ready ? "✓" : readiness.unresolved_total}
        tone={ready ? "ok" : "warn"}
      />
    </div>
  );
}

function Card({
  label,
  value,
  tone,
  testId,
}: {
  label: string;
  value: number | string;
  tone: "ok" | "warn" | "bad" | "muted";
  testId: string;
}) {
  const cls =
    tone === "ok"
      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-900"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-900"
      : "border-slate-200 bg-white text-slate-700";
  return (
    <div
      className={`rounded-lg border p-3 ${cls}`}
      data-testid={testId}
      data-tone={tone}
    >
      <div className="text-[11px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-semibold mt-0.5" data-testid={`${testId}-value`}>
        {value}
      </div>
    </div>
  );
}

/**
 * VALID and INVALID rows don't have a compare surface — they get a focused
 * confirmation dialog with only the backend-offered resolutions.
 */
function SimpleResolveDialog({
  row,
  open,
  onClose,
  jobId,
  onResolved,
}: {
  row: NormalizedRow;
  open: boolean;
  onClose: () => void;
  jobId: string;
  onResolved: () => void;
}) {
  const offered = offeredResolutionsFor(row.status, null);
  const [inFlight, setInFlight] = useState(false);
  const [conflict, setConflict] = useState(false);

  const submit = async (resolution: Resolution) => {
    if (inFlight) return;
    setInFlight(true);
    setConflict(false);
    try {
      await api.post(`/import/jobs/${jobId}/rows/${row.id}/resolve`, {
        resolution,
        if_version: row.version,
      });
      toast.success("Resolution saved");
      onResolved();
      onClose();
    } catch (e: unknown) {
      if (isSessionExpired(e)) return;
      const s = (e as { response?: { status?: number } })?.response?.status;
      if (s === 409) {
        setConflict(true);
        onResolved();
      } else {
        toast.error("Could not save resolution.");
      }
    } finally {
      setInFlight(false);
    }
  };

  const LABELS: Record<Resolution, string> = {
    skip: "Skip",
    create_anyway: "Create anyway",
    update_existing: "Update existing",
    manual_review: "Mark for manual review",
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : onClose())}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="simple-resolve-dialog" aria-describedby={`sr-desc-${row.id}`}>
        <DialogHeader>
          <DialogTitle>
            {row.status === "INVALID" ? "Invalid row" : "Row actions"} · #{row.rowIndex}
          </DialogTitle>
          <DialogDescription id={`sr-desc-${row.id}`}>
            {row.status === "INVALID"
              ? "This row has validation errors. The only server-accepted resolution is manual review."
              : "This row is valid. You can still opt out or mark for manual review."}
          </DialogDescription>
        </DialogHeader>

        {row.errors.length > 0 && (
          <ul
            className="rounded-md border border-rose-200 bg-rose-50 p-2 text-sm space-y-1"
            aria-live="polite"
            data-testid="simple-resolve-errors"
          >
            {row.errors.map((e, i) => (
              <li key={i} className="text-rose-800 flex items-start gap-1">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  {e.field ? <strong>{e.field}: </strong> : null}
                  {e.code}
                </span>
              </li>
            ))}
          </ul>
        )}

        {conflict && (
          <div
            role="alert"
            className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
            data-testid="simple-resolve-conflict"
          >
            This row changed elsewhere. Reload and try again.
          </div>
        )}

        <DialogFooter className="pt-2 gap-2">
          {offered.map((r) => (
            <Button
              key={r}
              size="sm"
              variant={r === "manual_review" ? "default" : "outline"}
              className="rounded-full"
              disabled={inFlight}
              onClick={() => submit(r)}
              data-testid={`simple-resolve-action-${r}`}
            >
              {LABELS[r]}
            </Button>
          ))}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
